/**
 * Extracts the YouTube video ID from various URL formats
 * Supports:
 * - https://youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://youtube.com/embed/VIDEO_ID
 * - https://youtube.com/shorts/VIDEO_ID
 * - URLs with additional query parameters (e.g., ?si=..., &t=30s)
 */
export const extractYouTubeVideoId = (url) => {
  if (!url) return null;

  // Pattern to match YouTube video IDs (11 characters, alphanumeric, _ and -)
  const patterns = [
    // youtu.be/VIDEO_ID or youtu.be/VIDEO_ID?params
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    // youtube.com/watch?v=VIDEO_ID or youtube.com/watch?other=value&v=VIDEO_ID
    /(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/,
    // youtube.com/embed/VIDEO_ID
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    // youtube.com/shorts/VIDEO_ID
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

/**
 * Converts any YouTube URL to a proper embed URL
 * Returns: https://www.youtube.com/embed/VIDEO_ID with parameters to minimize suggestions
 */
export const convertToEmbedUrl = (url) => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) {
    // Fallback: return original URL if we can't extract ID
    return url;
  }

  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&fs=1&iv_load_policy=3`;
};

/**
 * Validates if a URL is a valid YouTube video URL
 */
export const isValidYouTubeUrl = (url) => {
  return extractYouTubeVideoId(url) !== null;
};

/**
 * Normalizes any YouTube URL to the standard watch format
 * This ensures consistent URL storage and handling
 * @param {string} url - Any supported YouTube URL format
 * @returns {string} - Standard watch URL or original if not a YouTube URL
 */
export const normalizeYouTubeUrl = (url) => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) {
    return url;
  }
  return `https://www.youtube.com/watch?v=${videoId}`;
};

/**
 * Gets the thumbnail URL for a YouTube video
 * Quality options: default, mqdefault, hqdefault, sddefault, maxresdefault
 * Default changed to maxresdefault for highest quality thumbnails
 */
export const getYouTubeThumbnailUrl = (url, quality = 'maxresdefault') => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;

  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};

/**
 * State tracking for YouTube IFrame API loading
 */
let youtubeAPILoaded = false;
let youtubeAPIPromise = null;

/**
 * Dynamically loads the YouTube IFrame API
 * Returns a promise that resolves when the API is ready
 */
export const loadYouTubeAPI = () => {
  // If already loaded, return resolved promise
  if (youtubeAPILoaded) {
    return Promise.resolve();
  }

  // If loading is in progress, return existing promise
  if (youtubeAPIPromise) {
    return youtubeAPIPromise;
  }

  // Start loading the API
  youtubeAPIPromise = new Promise((resolve, reject) => {
    // Check if API is already loaded by another script
    if (window.YT && window.YT.Player) {
      youtubeAPILoaded = true;
      resolve();
      return;
    }

    // Create script element
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;

    // YouTube API calls this global function when ready
    window.onYouTubeIframeAPIReady = () => {
      youtubeAPILoaded = true;
      resolve();
    };

    script.onerror = () => {
      youtubeAPIPromise = null; // Reset so it can be retried
      reject(new Error('Failed to load YouTube IFrame API'));
    };

    document.body.appendChild(script);
  });

  return youtubeAPIPromise;
};
